// Entité de marque + auteurs, source unique pour les données structurées JSON-LD.
// Centralisé ici pour garantir la COHÉRENCE D'ENTITÉ : un seul nom canonique, un seul
// jeu de `sameAs`, réutilisés par la home (Organization) et le blog (publisher + author).
// Une entité cohérente est ce qui permet à Google (Knowledge Graph) et aux moteurs IA
// (ChatGPT, Perplexity, Gemini) de résoudre « Velko POS » vers une marque connue et de
// la citer avec confiance.
import { BRAND, BRAND_LOGO_URL, BRAND_SAMEAS } from './brand';

const SCHEMA_CONTEXT = 'https://schema.org';

/** Pays d'Afrique francophone desservis (codes ISO 3166-1 alpha-2). */
export const AREA_SERVED = ['CM', 'CI', 'SN', 'BF', 'ML', 'GN', 'CD', 'CG'];

/**
 * Organization — entité racine de la marque. `name` = nom canonique (aligné sur le
 * SoftwareApplication pour ne pas scinder l'entité), `alternateName` = variante courte.
 */
export const ORGANIZATION_JSONLD: Record<string, unknown> = {
  '@context': SCHEMA_CONTEXT,
  '@type': 'Organization',
  name: BRAND.productName,
  alternateName: BRAND.name,
  url: BRAND.url,
  logo: BRAND_LOGO_URL,
  description: 'Logiciel de caisse (point de vente) conçu pour les commerçants africains.',
  foundingDate: '2025',
  foundingLocation: { '@type': 'Place', name: 'Yaoundé, Cameroun' },
  address: { '@type': 'PostalAddress', addressLocality: 'Yaoundé', addressCountry: 'CM' },
  areaServed: AREA_SERVED,
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    telephone: BRAND.phone,
    email: BRAND.email,
    availableLanguage: ['French'],
    areaServed: AREA_SERVED,
  },
  sameAs: BRAND_SAMEAS,
};

/** Bloc `publisher` réutilisable (BlogPosting, etc.). */
export const PUBLISHER_JSONLD: Record<string, unknown> = {
  '@type': 'Organization',
  name: BRAND.productName,
  url: BRAND.url,
  logo: { '@type': 'ImageObject', url: BRAND_LOGO_URL },
};

/** SoftwareApplication — le produit lui-même (page d'accueil). */
export const SOFTWARE_APP_JSONLD: Record<string, unknown> = {
  '@context': SCHEMA_CONTEXT,
  '@type': 'SoftwareApplication',
  name: BRAND.productName,
  description:
    'Logiciel de caisse offline-first pour commerçants africains : ventes, stock, multi-boutique et paiements Mobile Money (Orange Money, MTN MoMo, Wave).',
  url: BRAND.url,
  operatingSystem: 'Web, Windows, macOS, Android',
  applicationCategory: 'BusinessApplication',
  inLanguage: 'fr',
  image: `${BRAND.url}/brand/og/og-home.png`,
  featureList: [
    'Ventes et encaissement',
    'Gestion de stock et inventaire',
    'Catalogue produits',
    'Multi-boutique',
    'Mode hors ligne',
    'Paiements Mobile Money (Orange Money, MTN MoMo, Wave)',
    'Gestion d’équipe et rôles',
    'Rapports et tableaux de bord',
  ],
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'XOF' },
  publisher: { '@type': 'Organization', name: BRAND.productName, url: BRAND.url },
};

// ---------------------------------------------------------------------------
// Auteurs du blog — signal E-E-A-T (Expérience / Expertise / Autorité).
// Un article signé par une PERSONNE nommée (rôle, bio, profils, domaines d'expertise)
// est plus crédible et plus citable par les IA qu'un article signé par la marque.
// ---------------------------------------------------------------------------

export interface BlogAuthor {
  /** Identifiant stable (slug). */
  key: string;
  name: string;
  /** Fonction affichée (ex. « Fondateur de Velko POS »). */
  role: string;
  bio: string;
  /** Profils publics de l'auteur (LinkedIn, X…) → `sameAs` du schéma Person. */
  sameAs?: string[];
  /** Domaines d'expertise → `knowsAbout` (renforce l'autorité thématique). */
  knowsAbout?: string[];
}

// Auteur par défaut (fondateur). Pas de profil personnel public → pas de `sameAs`
// (le `worksFor` du schéma relie déjà l'auteur à l'entité Velko POS).
const FOUNDER: BlogAuthor = {
  key: 'douglace-kenkeu',
  name: 'Douglace Kenkeu',
  role: 'Fondateur de Velko POS',
  bio: 'Fondateur de Velko POS. Depuis Yaoundé, il construit une caisse pensée pour le commerce africain : mode hors ligne, Mobile Money (Orange, MTN, Wave) et multi-boutique. Il partage ici ce qu’il apprend au contact des commerçants au Cameroun, en Côte d’Ivoire et au Sénégal.',
  knowsAbout: [
    'logiciel de caisse',
    'point de vente',
    'Mobile Money',
    'gestion de stock',
    'commerce de détail en Afrique',
  ],
};

export const AUTHORS: Record<string, BlogAuthor> = {
  [FOUNDER.key]: FOUNDER,
};

const DEFAULT_AUTHOR = FOUNDER;

/**
 * Résout l'auteur d'un article (chaîne libre des JSON) vers une fiche structurée.
 * Match par clé ou par nom ; à défaut, retombe sur l'auteur par défaut (fondateur),
 * de sorte que les articles existants gagnent immédiatement un auteur humain.
 */
export function resolveAuthor(author?: string): BlogAuthor {
  if (!author) return DEFAULT_AUTHOR;
  const norm = author.trim().toLowerCase();
  const found = Object.values(AUTHORS).find(
    (a) => a.key === norm || a.name.toLowerCase() === norm,
  );
  return found ?? DEFAULT_AUTHOR;
}

/** Schéma `Person` pour le champ `author` d'un BlogPosting. */
export function authorJsonLd(a: BlogAuthor): Record<string, unknown> {
  return {
    '@type': 'Person',
    name: a.name,
    jobTitle: a.role,
    description: a.bio,
    ...(a.sameAs && a.sameAs.length > 0 ? { sameAs: a.sameAs } : {}),
    ...(a.knowsAbout && a.knowsAbout.length > 0 ? { knowsAbout: a.knowsAbout } : {}),
    worksFor: { '@type': 'Organization', name: BRAND.productName, url: BRAND.url },
  };
}
